/**
 * Admin > QA Forms - Phase QA-1 (2026-07-02).
 *
 * List / create / edit / delete QA evaluation forms. Sections + questions
 * are edited inline; save writes the whole tree atomically.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { qaApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';


const KINDS = [
  { v: 'yes_no',       l: 'Yes/No' },
  { v: 'scale_1_5',    l: 'Scale 1-5' },
  { v: 'multi_choice', l: 'Multiple choice' },
  { v: 'free_text',    l: 'Free text' },
];


function emptyForm() {
  return {
    name: '',
    description: '',
    active: true,
    passing_score: null,
    sections: [
      { title: 'General', description: '', weight: 1.0, questions: [] },
    ],
  };
}


export default function QaFormsPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | 'new' | form object

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await qaApi.listForms(false);
      setForms(list || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onEdit = async (id) => {
    const f = await qaApi.getForm(id);
    setEditing(f);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">QA Forms</h1>
        <div className="flex gap-2">
          <HelpButton articleId="admin_qa_forms" />
          <button
            onClick={() => setEditing(emptyForm())}
            className="px-3 py-1.5 rounded-thin bg-continuum-600 text-white text-sm hover:bg-continuum-700"
          >+ New Form</button>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="bg-white dark:bg-neutral-900 rounded-thin border border-surface-border dark:border-surface-dark-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-neutral-800">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-left px-3 py-2">Sections</th>
                <th className="text-left px-3 py-2">Questions</th>
                <th className="text-left px-3 py-2">Active</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {forms.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-4 text-center opacity-60">No forms yet.</td></tr>
              ) : forms.map(f => (
                <tr key={f.id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2 font-medium">{f.name}</td>
                  <td className="px-3 py-2 opacity-80">{f.description}</td>
                  <td className="px-3 py-2">{f.section_count}</td>
                  <td className="px-3 py-2">{f.question_count}</td>
                  <td className="px-3 py-2">{f.active ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onEdit(f.id)} className="text-continuum-600 dark:text-continuum-200 text-sm mr-3">Edit</button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete form "${f.name}"?`)) return;
                        try {
                          await qaApi.deleteForm(f.id);
                          refresh();
                        } catch (err) {
                          alert('Delete failed: ' + (err?.detail || err?.message));
                        }
                      }}
                      className="text-red-600 text-sm"
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <FormEditor
          initial={editing === 'new' ? emptyForm() : editing}
          onSaved={() => { setEditing(null); refresh(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}


function FormEditor({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(initial || {})));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isNew = !form.id;

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const addSection = () => {
    setForm(prev => ({
      ...prev,
      sections: [
        ...(prev.sections || []),
        { title: 'New Section', description: '', weight: 1.0, questions: [] },
      ],
    }));
  };

  const patchSection = (idx, patch) => {
    setForm(prev => {
      const sections = [...prev.sections];
      sections[idx] = { ...sections[idx], ...patch };
      return { ...prev, sections };
    });
  };

  const removeSection = (idx) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== idx),
    }));
  };

  const addQuestion = (sIdx) => {
    patchSection(sIdx, {
      questions: [
        ...(form.sections[sIdx].questions || []),
        { text: '', kind: 'yes_no', weight: 1.0, required: true, auto_fail: false, choices: null },
      ],
    });
  };

  const patchQuestion = (sIdx, qIdx, patch) => {
    setForm(prev => {
      const sections = [...prev.sections];
      const questions = [...(sections[sIdx].questions || [])];
      questions[qIdx] = { ...questions[qIdx], ...patch };
      sections[sIdx] = { ...sections[sIdx], questions };
      return { ...prev, sections };
    });
  };

  const removeQuestion = (sIdx, qIdx) => {
    setForm(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = {
        ...sections[sIdx],
        questions: sections[sIdx].questions.filter((_, i) => i !== qIdx),
      };
      return { ...prev, sections };
    });
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = {
        name: form.name,
        description: form.description || null,
        active: form.active !== false,
        passing_score: form.passing_score,
        sections: (form.sections || []).map((s, si) => ({
          display_order: si,
          title: s.title,
          description: s.description || null,
          weight: s.weight || 1.0,
          questions: (s.questions || []).map((q, qi) => ({
            display_order: qi,
            text: q.text,
            kind: q.kind,
            weight: q.weight || 1.0,
            required: q.required !== false,
            auto_fail: !!q.auto_fail,
            choices: q.kind === 'multi_choice' ? (q.choices || []) : null,
          })),
        })),
      };
      if (isNew) {
        await qaApi.createForm(body);
      } else {
        await qaApi.updateForm(form.id, body);
      }
      onSaved();
    } catch (err) {
      setError(err?.detail || err?.message || 'save_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white dark:bg-neutral-900 rounded-thin border border-surface-border dark:border-surface-dark-border w-full max-w-4xl my-8">
        <div className="p-4 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <div className="text-lg font-semibold">{isNew ? 'New QA Form' : `Edit: ${form.name}`}</div>
          <button onClick={onCancel} className="text-sm opacity-70 hover:opacity-100">Close</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs opacity-70 mb-1">Name *</span>
              <input
                value={form.name || ''}
                onChange={e => setField('name', e.target.value)}
                className="w-full bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-xs opacity-70 mb-1">Passing score (0-100)</span>
              <input
                type="number"
                value={form.passing_score ?? ''}
                onChange={e => setField('passing_score', e.target.value === '' ? null : Number(e.target.value))}
                className="w-full bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-sm"
              />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs opacity-70 mb-1">Description</span>
            <textarea
              value={form.description || ''}
              onChange={e => setField('description', e.target.value)}
              rows={2}
              className="w-full bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={e => setField('active', e.target.checked)}
            />
            <span>Active (visible to evaluators)</span>
          </label>

          <div className="space-y-3">
            {(form.sections || []).map((s, si) => (
              <div key={si} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-3">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    value={s.title || ''}
                    onChange={e => patchSection(si, { title: e.target.value })}
                    className="flex-1 bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1.5 text-sm font-medium"
                    placeholder="Section title"
                  />
                  <label className="text-xs">
                    Weight
                    <input
                      type="number"
                      step="0.1"
                      value={s.weight || 1}
                      onChange={e => patchSection(si, { weight: Number(e.target.value) })}
                      className="ml-1 w-16 bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1 text-xs"
                    />
                  </label>
                  <button onClick={() => removeSection(si)} className="text-red-500 text-xs">Remove</button>
                </div>
                <textarea
                  value={s.description || ''}
                  onChange={e => patchSection(si, { description: e.target.value })}
                  rows={1}
                  placeholder="Optional section description"
                  className="w-full bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1.5 text-xs mb-2"
                />
                <div className="space-y-2">
                  {(s.questions || []).map((q, qi) => (
                    <QuestionEditor
                      key={qi}
                      question={q}
                      onChange={patch => patchQuestion(si, qi, patch)}
                      onRemove={() => removeQuestion(si, qi)}
                    />
                  ))}
                </div>
                <button onClick={() => addQuestion(si)} className="mt-2 text-xs text-continuum-600 dark:text-continuum-200">+ Add question</button>
              </div>
            ))}
            <button onClick={addSection} className="text-sm text-continuum-600 dark:text-continuum-200">+ Add section</button>
          </div>

          {error && <div className="text-sm text-red-500">Error: {String(error)}</div>}
        </div>
        <div className="p-4 border-t border-surface-border dark:border-surface-dark-border flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !form.name}
            className="px-3 py-1.5 rounded-thin bg-continuum-600 text-white text-sm hover:bg-continuum-700 disabled:opacity-50"
          >{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}


function QuestionEditor({ question, onChange, onRemove }) {
  const q = question;
  const [choicesText, setChoicesText] = useState(
    q.kind === 'multi_choice' && Array.isArray(q.choices)
      ? q.choices.map(c => `${c.value}|${c.label || c.value}|${c.score != null ? c.score : ''}`).join('\n')
      : ''
  );

  const applyChoices = (raw) => {
    setChoicesText(raw);
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    const parsed = lines.map(l => {
      const parts = l.split('|').map(p => p.trim());
      const [value, label, score] = parts;
      return {
        value: value || label,
        label: label || value,
        score: score !== undefined && score !== '' ? Number(score) : null,
      };
    });
    onChange({ choices: parsed });
  };

  return (
    <div className="bg-surface-subtle/50 dark:bg-neutral-800/50 border border-surface-border dark:border-surface-dark-border rounded-thin p-2">
      <div className="flex gap-2 mb-1">
        <input
          value={q.text || ''}
          onChange={e => onChange({ text: e.target.value })}
          placeholder="Question text"
          className="flex-1 bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1.5 text-sm"
        />
        <select
          value={q.kind}
          onChange={e => onChange({ kind: e.target.value })}
          className="bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1.5 text-xs"
        >
          {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
        <input
          type="number"
          step="0.1"
          value={q.weight || 1}
          onChange={e => onChange({ weight: Number(e.target.value) })}
          className="w-16 bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1.5 text-xs"
          title="Weight"
        />
        <button onClick={onRemove} className="text-red-500 text-xs">x</button>
      </div>
      <div className="flex gap-3 text-xs mb-1">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={q.required !== false} onChange={e => onChange({ required: e.target.checked })} />
          Required
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={!!q.auto_fail} onChange={e => onChange({ auto_fail: e.target.checked })} />
          Auto-fail on no/low
        </label>
      </div>
      {q.kind === 'multi_choice' && (
        <div>
          <div className="text-[10px] opacity-70 mb-0.5">Choices - one per line: value|label|score</div>
          <textarea
            value={choicesText}
            onChange={e => applyChoices(e.target.value)}
            rows={3}
            className="w-full bg-transparent border border-surface-border dark:border-surface-dark-border rounded-thin p-1.5 text-xs font-mono"
            placeholder={"excellent|Excellent|100\ngood|Good|75\nfair|Fair|50\npoor|Poor|0"}
          />
        </div>
      )}
    </div>
  );
}
